// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useMutation } from '@apollo/client';
import { mutateRelatedConciergeMoves, UpdateReferenceNumOnRelatedConciergeMovesDocument } from '@hopdrive/feature-move-planner';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

import { useCustomerConfig, useUser } from '@store';
import { Moves } from '@gql/base-types';

import { formatMoveId, getFormattedVehicleFromMove } from '@services/moveService';
import { isMoveLite } from '@utils/tiers';

import { css } from '@emotion/css';
import { Theme, useTheme, Button, InputAdornment, LinearProgress, TextField, Typography } from '@mui/material';
import { BiClipboard } from 'react-icons/bi';

import { ModalHeader, ModalContent, ModalFooter } from '@components/ModalComponents';
import Spacer from '@components/Utils/Spacer';

// TYPES ---------------------------------------------------------------- //

interface AssignRefModalContentProps {
  onClose?: (output?: any) => void;
  move?: Moves | null;
}

// COMPONENT ---------------------------------------------------------------- //

const AssignRefModalContent = ({ onClose, move }: AssignRefModalContentProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const user = useUser();
  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [referenceNum, setReferenceNum] = React.useState<string>(move?.reference_num || '');
  const [validation, setValidation] = React.useState<{ referenceNum?: boolean }>({
    referenceNum: true,
  });

  const [updateReferenceNumOnRelatedConciergeMoves] = useMutation(UpdateReferenceNumOnRelatedConciergeMovesDocument);

  // This content mounts only while the modal is open, so no `open` check is needed.
  const holdsUnsavedWork = referenceNum !== (move?.reference_num || '') || isSaving;
  useReloadGuard(holdsUnsavedWork, 'Reference number');

  const handleValidation = () => {
    const newValidation = { referenceNum: true };

    if (!referenceNum || referenceNum.trim() === '') {
      newValidation.referenceNum = false;
    }

    setValidation(newValidation);
    return newValidation;
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    const validationResult = handleValidation();
    const isValid = Object.values(validationResult).every(Boolean);

    if (!isValid) {
      toast.warning(`Please enter the ${refLabel}.`, {
        toastId: 'ref-warning',
      });
      setIsSaving(false);
      return;
    }

    try {
      const success = await mutateRelatedConciergeMoves(
        updateReferenceNumOnRelatedConciergeMoves,
        [move?.id || 0],
        referenceNum.trim(),
        user?.profile?.email || 'system'
      );

      if (success) {
        toast.success(
          `Successfully updated ${refLabel} for Move ${formatMoveId(move?.id || 0, { isLite: isMoveLite(move) })}.`,
          { toastId: 'update-ref-success' }
        );
        handleClose({ referenceNum: referenceNum.trim() });
      } else {
        console.error(`Failed to update ${refLabel} for Move #${move?.id}!`);
        toast.error(`Failed to update ${refLabel} for Move #${move?.id}!`, {
          toastId: 'update-ref-error',
        });
      }
    } catch (err: any) {
      Sentry.captureException(err);
      console.error(`Failed to update ${refLabel} for Move #${move?.id}:`, err);
      toast.error(`Failed to update ${refLabel} for Move #${move?.id}!`, {
        toastId: 'update-ref-error',
      });
    }

    setIsSaving(false);
  };

  const handleClose = (output?: any) => {
    if (onClose) onClose(output);
  };

  return (
    <>
      <ModalHeader handleClose={handleClose}>
        Assign {refLabel} (Move {formatMoveId(move?.id || 0, { isLite: move?.lite })})
      </ModalHeader>

      <ModalContent subtitle={`Enter the ${refLabel} for this move.`}>
        {/* Time Requested Time Box */}
        <>
          <div className={cls.infoBox}>
            {move?.ready_by ? (
              <>
                <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary }}>
                  Time Requested
                </Typography>

                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                  {dayjs(move.ready_by).format('MMMM D, YYYY [at] h:mm A')}
                </Typography>
              </>
            ) : null}

            {move?.pickup_started ? (
              <>
                <Spacer />

                <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary }}>
                  Picked Up At
                </Typography>

                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {dayjs(move.pickup_started).format('MMMM D, YYYY [at] h:mm A')}
                </Typography>
              </>
            ) : null}

            {move?.delivery_successful ? (
              <>
                <Spacer />

                <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary }}>
                  Delivered At
                </Typography>

                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {dayjs(move.delivery_successful).format('MMMM D, YYYY [at] h:mm A')}
                </Typography>
              </>
            ) : null}
          </div>

          <Spacer />
        </>

        {/* Vehicle Info Box */}
        <div className={cls.infoBox}>
          <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary }}>
            Vehicle Information
          </Typography>

          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            {getFormattedVehicleFromMove(move)}
          </Typography>

          {move?.vehicle_vin && <Typography variant='body2'>VIN: {move.vehicle_vin}</Typography>}

          {move?.vehicle_stock && <Typography variant='body2'>Stock: {move.vehicle_stock}</Typography>}
        </div>

        <Spacer size={3} />

        {/* Reference ID Input */}
        <TextField
          fullWidth
          label={refLabel}
          value={referenceNum}
          onChange={e => setReferenceNum(e.target.value)}
          error={!validation?.referenceNum}
          helperText={validation?.referenceNum ? '' : `Please enter the ${refLabel}!`}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <BiClipboard size={20} />
                </InputAdornment>
              ),
            },
          }}
        />
      </ModalContent>

      {isSaving ? <LinearProgress /> : null}

      <ModalFooter>
        <Button variant='text' color='utility' onClick={() => handleClose()}>
          Cancel
        </Button>
        <Button disabled={isSaving} color='primary' onClick={handleSubmit}>
          Submit
        </Button>
      </ModalFooter>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => {
  const styles = {
    infoBox: css`
      padding: 16px;
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 10px;
      background-color: ${theme?.palette?.action?.hover};
    `,
  };

  return styles;
};

// EXPORT ---------------------------------------------------------------- //

export default AssignRefModalContent;
